import { Modal, Radio, Input, Form } from 'antd';
import { useEffect } from 'react';
import type { RecordReviewBody } from '@/api/cases';

/**
 * 记录审查结果弹窗（reviewing → approved/rejected）。
 *
 * 后端契约：POST /declaration-cases/{id}/record-review
 *   body: { result: "approved" | "rejected", review_comments?: string }
 *
 * 注：成功后是终止态写入；前端 onSubmit 由调用方负责调 API + invalidate。
 */
export interface RecordReviewModalProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (body: RecordReviewBody) => void;
}

export function RecordReviewModal({
  open,
  loading,
  onCancel,
  onSubmit,
}: RecordReviewModalProps) {
  const [form] = Form.useForm<RecordReviewBody>();

  // 打开时重置；关闭时不动（避免闪烁）
  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  return (
    <Modal
      title="记录审查结果"
      open={open}
      onCancel={onCancel}
      okText="提交"
      cancelText="取消"
      confirmLoading={loading}
      onOk={async () => {
        try {
          const values = await form.validateFields();
          onSubmit({
            result: values.result,
            review_comments: values.review_comments?.trim() || undefined,
          });
        } catch {
          // antd validate 失败不抛
        }
      }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ result: 'approved' }}
        preserve={false}
      >
        <Form.Item
          name="result"
          label="审查结论"
          rules={[{ required: true, message: '请选择结论' }]}
        >
          <Radio.Group>
            <Radio value="approved">通过 approved</Radio>
            <Radio value="rejected">驳回 rejected</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="review_comments" label="审查意见（可选）">
          <Input.TextArea
            rows={4}
            placeholder="政府反馈意见 / 备注 / 整改要点"
            maxLength={2000}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
